import { isServiceApiPath, paneAskFields } from './paneHost.js';
import { PaneToolTurn } from './paneToolCalls.js';
import { parseQuestions } from './clarificationQuestions.js';
import { attentionMetadata } from './attention/metadata.js';
import { PAY_DATA_NOT_SHAREABLE_TEXT } from './share.js';
import { parseToolEventMeta } from './toolActivityLabels.js';
import { parsePetSetupMarker } from './petSetupHandoff.js';
import { parseLiveUsage, parseTurnUsage } from './tokenUsage.js';
import { isAttachmentRefusalCode, isFileRefusalReason } from './askFailure.js';
import { isMascotOutfitId, isMascotStars, MASCOT_THUMBNAILS_BATCH_MAX, parseMascotActionsPreview, parseMascotImage, parseMascotLooksView, parseMascotRatingAnswer, parseMascotThumbnail, parseMascotThumbnailBatch, } from './mascotLooks.js';
import { looksViewEtag, parseGyroLookAnswer } from './gyroLook.js';
import { PPP_REPORT_PATH, parsePppMe, pppDownloadName } from './ppp.js';
import { defaultPulseChips, parsePulseChips } from './pulse/pulseCards.js';
/** A fresh per-request operation id: a UUID where the runtime has one, else `op-<ms>-<random>`. */
export function newOperationId() {
    const c = globalThis.crypto;
    if (typeof c?.randomUUID === 'function')
        return c.randomUUID();
    return `op-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}
/** Best-effort IANA timezone of this client — sent with asks so the brain can resolve "tomorrow at 1pm". */
function clientTimeZone() {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
    }
    catch {
        return undefined;
    }
}
/** `path`, with `params` and the person's IANA zone as `tz` when this browser names one (day outfits judge the day there). */
function withZone(path, params = new URLSearchParams()) {
    const tz = clientTimeZone();
    if (tz)
        params.set('tz', tz);
    const query = params.toString();
    return query ? `${path}?${query}` : path;
}
export class GyroActionResponseError extends Error {
    statusCode;
    reconciliationRequired;
    constructor(message, statusCode, reconciliationRequired) {
        super(message);
        this.statusCode = statusCode;
        this.reconciliationRequired = reconciliationRequired;
        this.name = 'GyroActionResponseError';
    }
}
export const PULSE_COMMENT_MAX_CHARS = 500;
export const PULSE_TAGS_MAX = 3;
/** How long a tool's cause chips are kept on the client (contract section 10.4: cached an hour). */
export const PULSE_CHIPS_TTL_MS = 60 * 60 * 1000;
/** The most pages one answer names (the service's cap). */
export const STARSCRIBE_CREATED_MAX = 5;
const STARSCRIBE_PAGE_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** Whether a value is a StarScribe page id (a UUID, as the service mints them). */
export function isStarscribePageId(value) {
    return typeof value === 'string' && STARSCRIBE_PAGE_ID.test(value);
}
/**
 * An answer's `starscribe: { created: [{ pageId, title, open }] }`: the valid pages, in order, at most
 * STARSCRIBE_CREATED_MAX; undefined when it names none. Anything malformed shows nothing.
 */
export function parseStarscribeCreated(raw) {
    const list = raw && typeof raw === 'object' ? raw.created : undefined;
    if (!Array.isArray(list))
        return undefined;
    const created = [];
    for (const entry of list.slice(0, STARSCRIBE_CREATED_MAX)) {
        const value = entry && typeof entry === 'object' ? entry : {};
        const title = typeof value.title === 'string' ? value.title.trim() : '';
        if (!isStarscribePageId(value.pageId) || !title || title.length > 200)
            continue;
        created.push({ pageId: value.pageId.toLowerCase(), title, open: value.open === true });
    }
    return created.length ? { created } : undefined;
}
/** The AskResult field for an answer's raw `starscribe`: present only when it names a valid page. */
function starscribeCreatedField(raw) {
    const starscribe = parseStarscribeCreated(raw);
    return starscribe ? { starscribe } : {};
}
/**
 * A refused StarScribe request: the HTTP status, the service's code (`starscribe_read_only`, `starscribe_too_long`,
 * `starscribe_resync`, ...; `request_failed` when it sent none) and its sentence, or a plain one.
 */
export class StarscribeRequestError extends Error {
    status;
    code;
    details;
    constructor(status, code, message, details) {
        super(message);
        this.status = status;
        this.code = code;
        this.details = details;
        this.name = 'StarscribeRequestError';
    }
}
async function starscribeRefusal(response) {
    const payload = await response.json().catch(() => null);
    const detail = payload && typeof payload === 'object' ? payload : {};
    const code = typeof detail.error === 'string' && /^[a-z0-9_]{1,64}$/.test(detail.error) ? detail.error : 'request_failed';
    const message = typeof detail.message === 'string' && detail.message.trim() ? detail.message.trim().slice(0, 500)
        : response.status === 401 ? 'Sign in again to use StarScribe.'
            : response.status === 403 ? 'Your account cannot do this in StarScribe.'
                : response.status === 404 ? 'This page is no longer available to you.'
                    : 'StarScribe could not complete this request. Try again.';
    return new StarscribeRequestError(response.status, code, message, detail.details);
}
/**
 * gyro-svc refused to voice a text (an answer from a chat that holds pay data), or held it back from its voice (an empty
 * 204: its speech filter found words that are never spoken): nothing may speak it. `message` is the pay refusal's sentence
 * unless given; a held-back text gets none, because the 204 carries no words and the pay sentence is not about it.
 */
export class TtsRefusedError extends Error {
    ttsRefused = true;
    constructor(message = 'This answer comes from a chat that holds pay data, so it is not read aloud.') {
        super(message);
        this.name = 'TtsRefusedError';
    }
}
/**
 * A failed mascot-gallery request: gyro-svc's error `code` and the HTTP `status`, or code `network` (status 0) /
 * `invalid_response` when no usable answer came back. `unavailable` is the 409 for a look that has left the gallery.
 */
export class MascotLookError extends Error {
    code;
    status;
    retryAfterMs;
    /** `retryAfterMs`: how long a 429 asked to wait (its retry-after header or body), when it said. */
    constructor(code, status, retryAfterMs) {
        super(`mascot looks ${status}${code ? `: ${code}` : ''}`);
        this.code = code;
        this.status = status;
        this.retryAfterMs = retryAfterMs;
        this.name = 'MascotLookError';
    }
    get unavailable() {
        return this.status === 409 && this.code === 'mascot_look_unavailable';
    }
}
/**
 * A rating that did not go through: gyro-svc's error `code` and the HTTP `status` (`network` with status 0 when no answer
 * came, `invalid_response` when one came that is not a rating's answer, `invalid_request` when the call was never sent),
 * and, for a 429, how long gyro-svc asked to wait. Its message names neither the outfit nor the stars.
 */
export class MascotRatingError extends Error {
    code;
    status;
    retryAfterMs;
    constructor(code, status, retryAfterMs) {
        super(`mascot rating ${status}${code ? `: ${code}` : ''}`);
        this.code = code;
        this.status = status;
        this.retryAfterMs = retryAfterMs;
        this.name = 'MascotRatingError';
    }
}
/**
 * Why a fork was refused, as a sentence for the person. Older gyro-svc builds have no fork route (404/405/501 without a
 * code of their own): the chat is kept and nothing else changes.
 */
export class ForkError extends Error {
    code;
    status;
    constructor(code, status) {
        const messages = {
            conversation_not_found: 'This chat is not in your saved history yet, so it could not be forked. Wait for it to save and try again.',
            conversation_too_large: 'This chat is too large to copy into a new chat. Fork from an earlier turn instead.',
            history_person_full: 'Your saved chats have reached their storage limit, so the fork was not made. Delete older chats to make room.',
            history_store_full: "Gyro's shared chat storage is full, so the fork was not made. Let your IT team know so they can make room.",
            rate_limited: 'Too many chat saves in a row. Wait a moment and try forking again.',
            fork_not_supported: 'This Gyro service needs an update before it can fork chats. Your chat has been kept.',
            history_unavailable: 'Your saved chats could not be read right now, so the fork was not made. Try again in a moment.',
        };
        super(Object.hasOwn(messages, code) ? messages[code] : status === 401 || status === 403
            ? 'Gyro could not verify your access, so the fork was not made. Sign in again and retry.'
            : 'Gyro could not fork this chat right now. Your chat has been kept. Try again.');
        this.code = code;
        this.status = status;
        this.name = 'ForkError';
    }
}
/**
 * Refusals whose `message` gyro-svc writes itself, naming the limit that was hit (admitTurn: the spend cap or the
 * per-user cap with its value and the usage counted, or the rate limit and when to retry). Only these are shown.
 */
const COMPACTION_LIMIT_CODES = new Set(['budget_exhausted', 'rate_limited']);
/** A gyro-svc limit sentence fit to show: one plain line of reasonable length, else nothing. */
function limitDetail(code, detail) {
    if (!COMPACTION_LIMIT_CODES.has(code) || typeof detail !== 'string')
        return undefined;
    const text = detail.trim();
    return text && text.length <= 400 && !/[\u0000-\u001f\u007f<>]/.test(text) ? text : undefined;
}
/**
 * Safe product messages only: provider response bodies must never become UI error details. The one exception is the
 * sentence gyro-svc writes for its own usage limits (`detail`), which names the limit that refused the summary.
 */
export class CompactionError extends Error {
    code;
    status;
    constructor(code, status, detail) {
        const limit = limitDetail(code, detail);
        const messages = {
            input_too_large: 'This conversation is too large to summarize at once (up to 1,000 turns or 400 KB of text). Your original chat has been kept.',
            invalid_request: 'This conversation could not be summarized. Open a chat with completed messages and try again.',
            model_not_allowed: 'Your selected model is unavailable for this account. Choose an allowed model and try again.',
            model_not_configured: 'Gyro needs a configured model API key to summarize chats. Ask your administrator to check the model settings.',
            app_disabled: 'Gyro is disabled for this app. Your original chat has been kept.',
            budget_exhausted: 'Your Gyro usage limit has been reached. Wait for it to reset or ask your administrator to adjust it. Your original chat has been kept.',
            rate_limited: 'Too many requests. Wait a moment and try summarizing again. Your original chat has been kept.',
            conversation_not_found: 'This saved conversation could not be found for your account. Refresh your chats and try again.',
            content_blocked: 'Your organization’s data protection policy blocked this summary. Your original chat has been kept.',
            summary_timeout: 'The conversation summary timed out. Your original chat has been kept. Try again.',
            summary_incomplete: 'The model did not return a complete summary. Your original chat has been kept. Try again.',
            summary_cancelled: 'Summarizing was cancelled. Your original chat has been kept.',
            summary_not_supported: 'This Gyro service needs an update before it can compact chats. Your original chat has been kept.',
            history_unavailable: 'Your saved conversation could not be read right now. Your original chat has been kept. Try again in a moment.',
        };
        super(limit ? `Gyro could not summarize this chat: ${limit} Your original chat has been kept.` : Object.hasOwn(messages, code) ? messages[code] : status === 401 || status === 403
            ? 'Gyro could not verify your access. Reconnect or sign in again, then retry. Your original chat has been kept.'
            : 'Gyro could not summarize this chat right now. Your original chat has been kept. Try again.');
        this.code = code;
        this.status = status;
        this.name = 'CompactionError';
    }
}
/**
 * Server-side cap on the per-user memory note, in characters (mirrors MAX_MEMORY in
 * services/gyro-svc/src/memory.ts — the service silently truncates anything longer).
 * The in-widget editor uses it for its char counter + textarea maxLength.
 */
export const MEMORY_MAX_CHARS = 8000;
/**
 * History writes are chained (HistoryWriteQueue), so one that never settles would block every later save, rename, delete
 * and compaction handoff. Each gives up after this long and counts as a failed write (the chat is kept).
 */
export const HISTORY_WRITE_TIMEOUT_MS = 20_000;
/** Statuses that refuse the transcript itself (shape, size, storage full): the same bytes would be refused again. */
const HISTORY_REFUSAL_STATUSES = new Set([400, 413, 507]);
function historyWriteSignal() {
    return typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function' ? AbortSignal.timeout(HISTORY_WRITE_TIMEOUT_MS) : undefined;
}
/** The media type of an attached .skill package (userSkills.ts SKILL_PACKAGE_MEDIA_TYPE). */
export const SKILL_PACKAGE_MEDIA_TYPE = 'application/vnd.gyro.skill';
/** A saved skill's id as gyro-svc mints them: `u-` (personal) or `c-` (company) and 10 base36 characters (userSkills.ts). */
export function isSkillId(value) {
    return typeof value === 'string' && /^[uc]-[0-9a-z]{10}$/.test(value);
}
/**
 * A refused skill request. `code` is gyro-svc's error code (`skill_limit` with `limit` and `max` for a 413 over one of
 * the limits, `skills_custody_required` for a 403 from an identity the service did not verify, `skill_not_found` for a
 * 404, `invalid_request` for the wrong shape), `message` the service's own sentence when it sent one.
 */
export class SkillRequestError extends Error {
    code;
    status;
    detail;
    limit;
    max;
    /** `detail`: the service's own sentence, when it sent one. */
    constructor(code, status, detail, limit, max) {
        super(detail || `skills ${status}${code ? `: ${code}` : ''}`);
        this.code = code;
        this.status = status;
        this.detail = detail;
        this.limit = limit;
        this.max = max;
        this.name = 'SkillRequestError';
    }
    /** The service refused to save because this identity is not verified here (a host that proves only its app key). */
    get custodyRequired() {
        return this.status === 403 && this.code === 'skills_custody_required';
    }
}
async function skillRefusal(res) {
    const body = (await res.json().catch(() => null));
    const code = typeof body?.error === 'string' && /^[a-z0-9_]{1,64}$/.test(body.error) ? body.error : '';
    const message = typeof body?.message === 'string' ? body.message.slice(0, 300) : undefined;
    const limit = typeof body?.limit === 'string' ? body.limit : undefined;
    const max = typeof body?.max === 'number' ? body.max : undefined;
    return new SkillRequestError(code, res.status, message, limit, max);
}
function skillItem(raw) {
    if (!raw || typeof raw !== 'object')
        return null;
    const value = raw;
    if (typeof value.id !== 'string' || typeof value.name !== 'string' || (value.scope !== 'personal' && value.scope !== 'company'))
        return null;
    return {
        id: value.id,
        name: value.name,
        description: typeof value.description === 'string' ? value.description : '',
        aliases: Array.isArray(value.aliases) ? value.aliases.filter((alias) => typeof alias === 'string') : [],
        scope: value.scope,
        updatedAt: typeof value.updatedAt === 'string' ? value.updatedAt : '',
        ...(typeof value.body === 'string' ? { body: value.body } : {}),
        ...(typeof value.version === 'number' ? { version: value.version } : {}),
        ...(typeof value.authorName === 'string' && value.authorName ? { authorName: value.authorName } : {}),
    };
}
function savedSkill(raw) {
    const item = skillItem(raw);
    const value = (raw ?? {});
    if (!item || typeof value.body !== 'string')
        throw new SkillRequestError('invalid_response', 200);
    return { ...item, body: value.body, createdAt: typeof value.createdAt === 'string' ? value.createdAt : item.updatedAt, source: value.source === 'package' || value.source === 'admin' ? value.source : 'chat' };
}
export class GyroClient {
    opts;
    constructor(opts) {
        this.opts = opts;
    }
    /** Public service address used by help links; never exposes authentication options. */
    get baseUrl() { return this.opts.baseUrl; }
    /** This client's app key: the surface its asks address. */
    get app() {
        return this.opts.app;
    }
    /** The signed-in person's attention projection. This deliberately has no companion
     * dependency and cannot address arbitrary service paths or external URLs. */
    async attentionRequest(path, options = {}) {
        const method = options.method ?? 'GET';
        const receiptPath = /^\/v1\/attention\/read-receipts\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        const listPath = /^\/v1\/attention(?:\?cursor=v1\.[0-9]{1,4}\.[a-f0-9]{64})?$/;
        const questionAnswerPath = /^\/v1\/attention\/gyro-questions\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\/answer$/i;
        const questionReceiptPath = /^\/v1\/attention\/gyro-questions\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\/answer-receipts\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        if (!(method === 'GET' && (listPath.test(path) || receiptPath.test(path) || questionReceiptPath.test(path)) || method === 'POST' && (path === '/v1/attention/read' || questionAnswerPath.test(path))))
            throw new Error('Invalid attention API operation.');
        options.signal?.throwIfAborted();
        const headers = await this.headers(options.body !== undefined);
        options.signal?.throwIfAborted();
        if (method === 'POST') {
            if (!options.operationId || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(options.operationId))
                throw new Error('A stable attention request identity is required.');
            headers['Idempotency-Key'] = options.operationId;
        }
        const timeout = AbortSignal.timeout(30000);
        const response = await fetch(`${this.opts.baseUrl}${path}${path.includes('?') ? '&' : '?'}${new URLSearchParams({ app: this.opts.app })}`, {
            method, headers, credentials: 'include', signal: options.signal ? AbortSignal.any([options.signal, timeout]) : timeout,
            ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
        });
        if (!response.ok) {
            const payload = await response.json().catch(() => null);
            const error = new Error(response.status === 401 ? 'Sign in again to see your notifications.'
                : response.status === 409 ? 'This item changed. Refresh it before continuing.'
                    : 'The notification center could not complete this request.');
            Object.assign(error, { status: response.status,
                code: payload && typeof payload === 'object' && 'error' in payload && typeof payload.error === 'string' ? payload.error : 'attention_request_failed' });
            throw error;
        }
        return response.json();
    }
    /**
     * The usage record's batch (usage/featureEvents.ts): `POST /v1/usage/events` with this client's own auth headers and
     * cookies, answering the HTTP status for the capture to judge (2xx sent, 400/413/422 dropped, the rest kept). Never
     * throws for a refusal; a network failure rejects. `keepalive` lets a batch sent on page hide complete.
     */
    async postUsageEvents(body, options = {}) {
        const headers = await this.headers(true);
        const response = await fetch(`${this.opts.baseUrl}/v1/usage/events?${new URLSearchParams({ app: this.opts.app })}`, {
            method: 'POST', headers, credentials: 'include', body: JSON.stringify(body), keepalive: options.keepalive === true, signal: AbortSignal.timeout(30000),
        });
        return response.status;
    }
    petAssetUrl(path) {
        const owned = /^\/v1\/pets\/[a-f0-9]{8}(?:-[a-f0-9]{4}){3}-[a-f0-9]{12}\/appearance\/static\?formId=[A-Za-z0-9][A-Za-z0-9_-]{0,79}&artRevision=[A-Za-z0-9][A-Za-z0-9_-]{0,79}&equipmentRevision=(0|[1-9][0-9]{0,15})&sha256=[a-f0-9]{64}$/.exec(path);
        if (!/^\/pets\/avatars\/[a-z0-9-]+\.png(?:\?v=[a-fA-F0-9]{8,64})?$/.test(path)
            && (!owned || !Number.isSafeInteger(Number(owned[1]))))
            return undefined;
        try {
            const base = this.opts.baseUrl || globalThis.location?.origin;
            const url = owned ? new URL(`${base?.replace(/\/$/, '')}${path}`) : new URL(path, base);
            const local = ['localhost', '127.0.0.1', '[::1]'].includes(url.hostname);
            return !url.username && !url.password && (url.protocol === 'https:' || (url.protocol === 'http:' && local)) ? url.href : undefined;
        }
        catch {
            return undefined;
        }
    }
    /** Private artwork uses the same fresh custody as API reads, never an anonymous image URL. */
    async petAsset(path, signal) {
        const resolved = this.petAssetUrl(path);
        if (!resolved)
            throw new Error('Invalid companion artwork path.');
        const target = new URL(resolved);
        if (!path.startsWith('/v1/pets/') || this.opts.app === 'desktop')
            target.searchParams.set('app', this.opts.app);
        const timeout = AbortSignal.timeout(20000);
        const response = await fetch(target.href, { headers: await this.headers(false), credentials: 'include',
            cache: 'no-store', redirect: 'error', signal: signal ? AbortSignal.any([signal, timeout]) : timeout });
        if (response.status !== 200 || response.headers.get('content-type')?.split(';')[0].trim() !== 'image/png' || !response.body)
            throw new Error('Companion artwork is unavailable.');
        const maximum = 8 * 1024 * 1024;
        if (Number(response.headers.get('content-length')) > maximum) {
            await response.body.cancel();
            throw new Error('Companion artwork exceeds its limit.');
        }
        const reader = response.body.getReader(), parts = [];
        let bytes = 0;
        try {
            for (;;) {
                const next = await reader.read();
                if (next.done)
                    break;
                bytes += next.value.byteLength;
                if (bytes > maximum)
                    throw new Error('Companion artwork exceeds its limit.');
                parts.push(new Uint8Array(next.value));
            }
            if (signal?.aborted)
                throw new Error('Companion artwork request was cancelled.');
            return new Blob(parts, { type: 'image/png' });
        }
        finally {
            await reader.cancel().catch(() => undefined);
            reader.releaseLock();
        }
    }
    /** Pet API transport retains this surface's authenticated custody; it cannot call arbitrary URLs. */
    async petRequest(path, options = {}) {
        if (!/^\/v1\/(?:pets|pet-library)(?:\/[A-Za-z0-9_-]+)*$/.test(path))
            throw new Error('Invalid companion API path.');
        const method = options.method ?? 'GET';
        const monitorPath = /^\/v1\/pets\/[a-f0-9]{8}(?:-[a-f0-9]{4}){3}-[a-f0-9]{12}\/(?:monitor-status|monitor-preview(?:\/[a-f0-9]{8}(?:-[a-f0-9]{4}){3}-[a-f0-9]{12}(?:\/(?:prepare|confirm))?)?)$/.test(path);
        if (monitorPath && Object.keys(options.query ?? {}).length)
            throw new Error('Monitoring API paths do not accept query parameters.');
        const query = new URLSearchParams(monitorPath ? {} : { ...options.query, app: this.opts.app });
        const headers = await this.headers(options.body !== undefined, method !== 'GET');
        if (method !== 'GET')
            headers['Idempotency-Key'] = options.operationId ?? crypto.randomUUID();
        const timeout = AbortSignal.timeout(90000);
        const response = await fetch(`${this.opts.baseUrl}${path}${query.size ? `?${query}` : ''}`, {
            method, headers, credentials: 'include', signal: options.signal ? AbortSignal.any([options.signal, timeout]) : timeout,
            ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
        });
        if (!response.ok) {
            const payload = await response.json().catch(() => null);
            const detail = payload && typeof payload === 'object' ? payload : {};
            const error = new Error(typeof detail.message === 'string' ? detail.message.slice(0, 500)
                : response.status === 401 ? 'Sign in again to see your companions.'
                    : response.status === 403 ? 'Your account cannot perform this companion action.' : 'The companion service could not complete this request.');
            Object.assign(error, { status: response.status, code: typeof detail.error === 'string' ? detail.error : 'request_failed', details: detail.details });
            throw error;
        }
        return response.status === 204 ? undefined : response.json();
    }
    /** A companion event stream (the Daily Brief's change feed): the same custody as petRequest, the raw response for the caller to read. */
    async petEvents(path, options = {}) {
        if (!/^\/v1\/pets(?:\/[A-Za-z0-9_-]+)*\/stream$/.test(path))
            throw new Error('Invalid companion stream path.');
        const query = new URLSearchParams({ ...options.query, app: this.opts.app });
        const headers = await this.headers(false);
        return fetch(`${this.opts.baseUrl}${path}?${query}`, { method: 'GET', headers, credentials: 'include', ...(options.signal ? { signal: options.signal } : {}) });
    }
    /**
     * StarScribe's routes (docs/CONTRACT-starscribe-clients.md section 4), the petRequest shape: paths under /v1/starscribe only,
     * with this surface's custody and auth headers and its app key. A refusal throws StarscribeRequestError with the service's
     * code and sentence.
     */
    async starscribeRequest(path, options = {}) {
        if (!/^\/v1\/starscribe(?:\/[A-Za-z0-9_-]+)*$/.test(path))
            throw new Error('Invalid StarScribe path.');
        const method = options.method ?? 'GET';
        const query = new URLSearchParams({ ...options.query, app: this.opts.app });
        const headers = await this.headers(options.body !== undefined, method !== 'GET');
        if (method !== 'GET')
            headers['Idempotency-Key'] = options.operationId ?? crypto.randomUUID();
        const timeout = AbortSignal.timeout(90000);
        const response = await fetch(`${this.opts.baseUrl}${path}?${query}`, {
            method, headers, credentials: 'include', signal: options.signal ? AbortSignal.any([options.signal, timeout]) : timeout,
            ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
        });
        if (!response.ok)
            throw await starscribeRefusal(response);
        return response.status === 204 ? undefined : response.json();
    }
    /** A StarScribe file as bytes (a page's image): a GET with the same custody, path rule and refusals as starscribeRequest. */
    async starscribeBlob(path, options = {}) {
        if (!/^\/v1\/starscribe(?:\/[A-Za-z0-9_-]+)*$/.test(path))
            throw new Error('Invalid StarScribe path.');
        const query = new URLSearchParams({ app: this.opts.app });
        const timeout = AbortSignal.timeout(90000);
        const response = await fetch(`${this.opts.baseUrl}${path}?${query}`, {
            method: 'GET', headers: await this.headers(false), credentials: 'include', signal: options.signal ? AbortSignal.any([options.signal, timeout]) : timeout,
        });
        if (!response.ok)
            throw await starscribeRefusal(response);
        return response.blob();
    }
    /**
     * A page's image upload (POST /v1/starscribe/pages/:id/images, the raw bytes with their content type): answers `{ src }`, the
     * image's own route. The page sends an ArrayBuffer (the desktop shell's request path carries bytes, not a Blob); a Blob is
     * taken too. A refusal throws StarscribeRequestError with the service's message.
     */
    async starscribeUpload(path, body, contentType, options = {}) {
        if (!/^\/v1\/starscribe\/pages\/[0-9a-f-]{36}\/images(?:\/[A-Za-z0-9_-]+)?$/.test(path))
            throw new Error('Invalid StarScribe image path.');
        const query = new URLSearchParams({ app: this.opts.app });
        const headers = await this.headers(false, true);
        headers['content-type'] = contentType;
        headers['Idempotency-Key'] = crypto.randomUUID();
        const timeout = AbortSignal.timeout(90000);
        const response = await fetch(`${this.opts.baseUrl}${path}?${query}`, {
            method: 'POST', headers, body, credentials: 'include', signal: options.signal ? AbortSignal.any([options.signal, timeout]) : timeout,
        });
        if (!response.ok)
            throw await starscribeRefusal(response);
        const answer = await response.json().catch(() => null);
        if (typeof answer?.src !== 'string' || !answer.src)
            throw new StarscribeRequestError(response.status, 'invalid_response', 'StarScribe could not complete this request. Try again.');
        return { src: answer.src };
    }
    /** A StarScribe page's live stream (section 4): the same custody as starscribeRequest, the raw response for the caller to read. */
    async starscribeEvents(path, options = {}) {
        if (!/^\/v1\/starscribe\/pages\/[0-9a-f-]{36}\/stream$/.test(path))
            throw new Error('Invalid StarScribe stream path.');
        const query = new URLSearchParams({ ...options.query, app: this.opts.app });
        const headers = await this.headers(false);
        return fetch(`${this.opts.baseUrl}${path}?${query}`, { method: 'GET', headers, credentials: 'include', ...(options.signal ? { signal: options.signal } : {}) });
    }
    /**
     * Request headers: the host's auth headers, plus a JSON content-type ONLY when a body goes with them. On a
     * bodiless GET that header is what turns a simple cross-origin request into a CORS preflight, i.e. two round
     * trips per inbox poll for nothing.
     *
     * `mutation` (default: a request with a body is one) adds the host's per-request operation-id header when
     * `operationIdHeader` is set. A bodiless DELETE passes `true`; `POST /v1/action` and `/v1/action/undo` pass `false`
     * (the ledger keys those on `actionId`).
     */
    async headers(withBody = false, mutation = withBody) {
        const extra = (await this.opts.authHeaders?.()) ?? {};
        const headers = withBody ? { 'content-type': 'application/json', ...extra } : { ...extra };
        const name = this.opts.operationIdHeader?.trim();
        if (mutation && name)
            headers[name] = newOperationId();
        return headers;
    }
    /**
     * Build a DIAGNOSTIC Error from a failed response. Captures the HTTP status AND the server's
     * `{error,message}` body (e.g. `app_auth_required`, `unauthenticated`) so a caller that logs/forwards
     * it can say WHY it failed — not just "status 401". Attaches `status`/`kind` for programmatic checks.
     * Never throws itself (a body that can't be read still yields the status code).
     */
    async httpError(kind, res) {
        let detail = '';
        let parsed;
        try {
            const body = await res.text();
            if (body) {
                try {
                    const j = JSON.parse(body);
                    parsed = j;
                    // A file refusal travels as its closed-set code, never the server's sentence: the specific reason when the
                    // service sent a known one (gyro-svc v73+), else the error code. askFailure.ts turns either into fixed copy.
                    // The ask routes' rate-limit 429 carries its sentence (the limit, when to try again) in `text`, not `message`:
                    // it travels too, and askFailure.ts shows a 429's sentence to the person (PR #242 review follow-up).
                    detail = j.error === 'file_cannot_be_read' && typeof j.reason === 'string' && isFileRefusalReason(j.reason) ? j.reason
                        : isAttachmentRefusalCode(j.error) ? j.error
                            : j.message || (res.status === 429 && typeof j.text === 'string' ? j.text : '') || j.error || body.slice(0, 200);
                }
                catch {
                    // A 429 that is not gyro-svc's JSON (a proxy's page) keeps only its status: a 429's detail is shown to the person.
                    detail = res.status === 429 ? '' : body.slice(0, 200);
                }
            }
        }
        catch {
            /* body unreadable — the status code alone still beats a bare boolean */
        }
        const err = new Error(`${kind} ${res.status}${detail ? `: ${detail}` : ''}`);
        err.status = res.status;
        err.kind = kind;
        const retryAfterMs = parseRetryAfterMs(res.headers.get('retry-after'), parsed);
        if (retryAfterMs !== undefined)
            err.retryAfterMs = retryAfterMs;
        return err;
    }
    async status() {
        // `tiers=1`: this client reads `tier` on the entries (the composer's Fast | Balanced | High control, which never saves
        // the pick), so the service may list the High entry. The widget before the control, which put every entry in a model
        // dropdown and remembered the pick, sends no flag and is never shown High (gyro-svc v138).
        const res = await fetch(`${this.opts.baseUrl}${withZone('/v1/status', new URLSearchParams({ app: this.opts.app, tiers: '1' }))}`, { headers: await this.headers(), credentials: 'include' });
        if (!res.ok)
            throw await this.httpError('status', res);
        return res.json();
    }
    /**
     * GET /v1/ppp/me, read the way the welcome data is (the host's auth headers and cookies; on the desktop the agent
     * forwards it natively, in the pane the installed fetch carries custody). Null when the answer is not the shape
     * pppMeResponseSchema promises; a refusal or a failed request throws. The starter shows only for `enabled && eligible`.
     */
    async pppMe(options = {}) {
        const res = await fetch(`${this.opts.baseUrl}${withZone('/v1/ppp/me', new URLSearchParams({ app: this.opts.app }))}`, { headers: await this.headers(), credentials: 'include', signal: options.signal });
        if (!res.ok)
            throw await this.httpError('ppp status', res);
        return parsePppMe(await res.json());
    }
    /**
     * A stored PPP report's Word or PDF file (GET /v1/ppp/reports/<id>.docx|pdf, kept 13 months): the twin of
     * downloadArtifact, for when the 24-hour file is gone. Only that path shape is fetched. The file's name is the
     * Content-Disposition header's, else `<fileStem>.<ext>` from /v1/ppp/me's lastReport (the desktop's native forward may
     * not carry the header). The caller saves it the way it saves an artifact (an anchor with `download`).
     */
    async downloadPppReport(path, options = {}) {
        const match = typeof path === 'string' ? PPP_REPORT_PATH.exec(path) : null;
        if (!match)
            throw new Error('That is not a PPP report file.');
        const format = match[1];
        const res = await fetch(`${this.opts.baseUrl}${path}?app=${encodeURIComponent(this.opts.app)}`, { headers: await this.headers(), credentials: 'include', signal: options.signal });
        if (!res.ok) {
            const failure = await this.httpError('report download', res);
            if (res.status === 404)
                failure.message = "That PPP file is no longer available: say 'export my PPP again'.";
            throw failure;
        }
        let name = pppDownloadName(res.headers.get('content-disposition'), undefined, format);
        if (!name) {
            const me = await this.pppMe({ signal: options.signal }).catch(() => null);
            name = pppDownloadName(null, me?.lastReport && path.includes(me.lastReport.id) ? me.lastReport.fileStem : undefined, format) ?? `Gyro PPP.${format}`;
        }
        return { blob: await res.blob(), name };
    }
    /** Always obtain fresh host assertions. Do not expose an unauthenticated download URL. */
    async downloadArtifact(artifact, options = {}) {
        const res = await fetch(`${this.opts.baseUrl}/v1/artifacts/${encodeURIComponent(artifact.taskId)}/${encodeURIComponent(artifact.id)}?app=${encodeURIComponent(this.opts.app)}`, {
            headers: await this.headers(), credentials: 'include', signal: options.signal,
        });
        if (!res.ok)
            throw await this.httpError('file download', res);
        return res.blob();
    }
    async ask(payload) {
        const res = await fetch(`${this.opts.baseUrl}/v1/ask`, {
            method: 'POST',
            headers: await this.headers(true),
            credentials: 'include',
            body: JSON.stringify({ app: this.opts.app, tz: clientTimeZone(), ...payload }),
        });
        if (!res.ok)
            throw await this.httpError('ask', res);
        const { starscribe, ...result } = await res.json();
        return { ...result, attentionQuestionIds: undefined, attentionUnavailable: undefined, ...attentionMetadata(result), questions: parseQuestions(result.questions), usage: parseTurnUsage(result.usage) ?? undefined, ...starscribeCreatedField(starscribe) };
    }
    /**
     * Streaming ask over SSE. Invokes handlers as events arrive. Returns a cancel() that aborts the
     * fetch and tells the server to stop the in-flight turn.
     */
    askStream(payload, handlers) {
        const turnId = `t-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
        const ac = new AbortController();
        // The host's own tools (the Office pane) for THIS turn only, read once: the ask declares them and its pane_tool_call
        // events run them. Another caller sharing this client (the add-in's =GYRO.ASK cells) passes none and is offered none.
        const turnTools = handlers.clientTools;
        void (async () => {
            try {
                const res = await fetch(`${this.opts.baseUrl}/v1/ask/stream`, {
                    method: 'POST',
                    headers: await this.headers(true),
                    credentials: 'include',
                    body: JSON.stringify({ ...(await paneAskFields(turnTools)), app: this.opts.app, turnId, tz: clientTimeZone(), ...payload }),
                    signal: ac.signal,
                });
                if (!res.ok || !res.body) {
                    // Carry the server's reason (e.g. app_auth_required) through, not just the status number.
                    // Only a missing/unsupported endpoint proves the streaming turn was not accepted.
                    // An empty or disconnected response may conceal already-started SDK tool effects.
                    handlers.onError?.(res.ok ? `stream ${res.status}: no response body` : (await this.httpError('stream', res)).message, { allowBufferedFallback: [404, 405, 501].includes(res.status) });
                    return;
                }
                // The server short-circuits some cases (disabled / unconfigured / rate-limited) with plain
                // JSON, not SSE. Detect that and deliver it as a final result so the caller never hangs.
                const ct = res.headers.get('content-type') || '';
                if (!ct.includes('text/event-stream')) {
                    const j = (await res.json().catch(() => ({})));
                    handlers.onDone?.({
                        mode: j.mode ?? 'error',
                        text: String(j.text ?? 'Sorry — I could not reach the assistant.'),
                        model: j.model,
                        pendingActions: j.pendingActions ?? [],
                        autoActions: j.autoActions,
                        followups: j.followups,
                        questions: parseQuestions(j.questions),
                        ...attentionMetadata(j),
                        sources: j.sources,
                        grounded: j.grounded,
                        advisory: j.advisory,
                        ...(typeof j.tierNote === 'string' && j.tierNote ? { tierNote: j.tierNote } : {}),
                        traceId: j.traceId,
                        artifacts: j.artifacts,
                        ...(typeof j.askId === 'string' && j.askId ? { askId: j.askId } : {}),
                        runtime: j.runtime,
                        usage: parseTurnUsage(j.usage) ?? undefined,
                        ...starscribeCreatedField(j.starscribe),
                    });
                    return;
                }
                const reader = res.body.getReader();
                const decoder = new TextDecoder();
                let buf = '';
                let doneSeen = false;
                // Each pane_tool_call runs on the host and never holds this reader; a turn without tools ignores the event.
                const paneTools = turnTools ? new PaneToolTurn({ tools: turnTools, signal: ac.signal, post: (path, body) => this.postClientToolResult(path, body) }) : undefined;
                for (;;) {
                    const { done, value } = await reader.read();
                    if (done)
                        break;
                    buf += decoder.decode(value, { stream: true });
                    const frames = buf.split('\n\n');
                    buf = frames.pop() ?? '';
                    for (const frame of frames) {
                        const evLine = frame.split('\n').find((l) => l.startsWith('event:'));
                        const dataLine = frame.split('\n').find((l) => l.startsWith('data:'));
                        if (!evLine || !dataLine)
                            continue;
                        const event = evLine.slice(6).trim();
                        let data = {};
                        try {
                            data = JSON.parse(dataLine.slice(5).trim());
                        }
                        catch {
                            continue;
                        }
                        if (event === 'text')
                            handlers.onText?.(String(data.delta ?? ''));
                        else if (event === 'tool_call_started')
                            handlers.onToolStart?.(String(data.tool), String(data.summary ?? ''), data.riskTier, parseToolEventMeta(data));
                        else if (event === 'tool_result') {
                            const setup = parsePetSetupMarker(data.tool, data.ok, data.petSetup);
                            if (setup)
                                handlers.onToolResult?.(String(data.tool), Boolean(data.ok), parseToolEventMeta(data, !data.ok), setup);
                            else
                                handlers.onToolResult?.(String(data.tool), Boolean(data.ok), parseToolEventMeta(data, !data.ok));
                        }
                        else if (event === 'usage') {
                            // Validated figures only (non-negative integers, a finite cost, a known phase); anything else is dropped.
                            const usage = parseLiveUsage(data);
                            if (usage)
                                handlers.onUsage?.(usage);
                        }
                        else if (event === 'status')
                            handlers.onStatus?.(Number(data.elapsedMs ?? 0), Number(data.sinceUpstreamMs ?? 0));
                        else if (event === 'pane_tool_call')
                            paneTools?.dispatch(data);
                        else if (event === 'done') {
                            doneSeen = true;
                            handlers.onDone?.({ mode: data.mode === 'error' || data.mode === 'unconfigured' ? data.mode : 'ai', text: String(data.text ?? ''), model: data.model, pendingActions: data.pendingActions ?? [], autoActions: data.autoActions, followups: data.followups, questions: parseQuestions(data.questions), ...attentionMetadata(data), sources: data.sources, grounded: data.grounded, advisory: data.advisory, ...(typeof data.tierNote === 'string' && data.tierNote ? { tierNote: data.tierNote } : {}), traceId: data.traceId, artifacts: data.artifacts, runtime: data.runtime, usage: parseTurnUsage(data.usage) ?? undefined, ...starscribeCreatedField(data.starscribe), ...(data.classified === true ? { classified: true } : {}), ...(typeof data.askId === 'string' && data.askId ? { askId: data.askId } : {}) });
                        }
                        else if (event === 'error') {
                            doneSeen = true;
                            handlers.onError?.(String(data.message ?? 'stream error'));
                        }
                    }
                }
                // Stream ended without a terminal event — don't leave the caller hanging.
                if (!doneSeen && !ac.signal.aborted)
                    handlers.onError?.('stream ended unexpectedly');
            }
            catch (e) {
                if (!ac.signal.aborted)
                    handlers.onError?.(e instanceof Error ? e.message : 'stream failed');
            }
        })();
        return () => {
            ac.abort();
            // Send the same auth headers (signed assertion etc.) so cancel isn't 401'd for non-cookie clients.
            void this.headers(true)
                .then((h) => fetch(`${this.opts.baseUrl}/v1/ask/cancel`, { method: 'POST', headers: h, credentials: 'include', body: JSON.stringify({ app: this.opts.app, turnId }) }))
                .catch(() => undefined);
        };
    }
    /**
     * The answer as speech, or null when there is none (the caller may then use the browser's voice). Throws
     * TtsRefusedError when gyro-svc refuses to voice the text at all (an answer from a chat that holds pay data) or holds it
     * back (an empty 204): no voice may speak it then, the browser's included.
     */
    async tts(text, options = {}) {
        let res;
        // The chat the answer is from: gyro-svc refuses to voice anything from a chat that holds pay data, whatever its text.
        const chat = options.conversationId ? { conversationId: options.conversationId } : {};
        try {
            res = await fetch(`${this.opts.baseUrl}/v1/tts`, { method: 'POST', headers: await this.headers(true), credentials: 'include', body: JSON.stringify({ app: this.opts.app, text, ...chat }), signal: options.signal });
        }
        catch {
            return null;
        }
        if (res.status === 403) {
            const refusal = (await res.json().catch(() => ({})));
            if (refusal.error === 'pay_data_not_spoken')
                throw new TtsRefusedError();
            return null;
        }
        // An empty 204 is the speech filter holding the text back (gyro-core speechSensitive). No voice reads it, as for the pay
        // refusal, and the text still shows on screen (owner, 2026-11-01: "Stay silent"). Every other failure, 503
        // voice_unconfigured included, is "no audio" and may use the browser's voice.
        if (res.status === 204)
            throw new TtsRefusedError('');
        if (!res.ok)
            return null;
        try {
            return await res.blob();
        }
        catch {
            return null;
        }
    }
    /**
     * Confirm or cancel a proposed action (POST /v1/action). A confirm always says it knows gyro-svc's second confirmation
     * (`reconfirmSupported: true`): for a governed security or break-glass write the answer is then `confirm_again` with a
     * ticket (nothing ran), and the confirm that runs it sends that ticket back in `options.reconfirm`. `confirmation` is the
     * exact phrase the action's preview gave; the card sends it itself, the person never types it.
     */
    async action(actionId, decision, token, confirmation, options = {}) {
        const second = decision === 'confirm'
            ? { reconfirmSupported: true, ...(typeof options.reconfirm === 'string' ? { reconfirm: options.reconfirm } : {}) }
            : {};
        const res = await fetch(`${this.opts.baseUrl}/v1/action`, { method: 'POST', headers: await this.headers(true, false), credentials: 'include', body: JSON.stringify({ app: this.opts.app, actionId, decision, token, confirmation, ...second }) });
        const json = (await res.json().catch(() => ({})));
        if (!res.ok && json.status !== 'error') {
            throw new GyroActionResponseError(json.message || `action ${res.status}`, res.status, json.reconciliationRequired === true);
        }
        return { ...json, status: json.status ?? 'error', summary: json.summary ?? '' };
    }
    /**
     * An IT operator claims a support request from the widget's support card (desktop 0.3.9.5). The route needs the
     * enrolled-device custody chain the desktop overlay attaches natively (the browser cannot); the raw status and
     * body come back so the card can say who won (409 not_open carries `claimedBy`), or why not (403
     * support_operator_required, 401 sign-in). Never throws on an HTTP status — only on a network failure.
     */
    async claimSupportRequest(requestId) {
        const res = await fetch(`${this.opts.baseUrl}/v1/support/requests/${encodeURIComponent(requestId)}/claim`, {
            method: 'POST',
            headers: await this.headers(true),
            credentials: 'include',
            body: JSON.stringify({ app: this.opts.app }),
        });
        const body = await res.json().catch(() => ({}));
        return { status: res.status, body };
    }
    /**
     * The action's preview. A refusal (`error`) may carry `connect`, the vendor connection the person can start from the
     * card (a family and a capability, as the Bridge's person receiver sends it; approvalCard.ts previewConnect checks it).
     */
    async previewAction(actionId, token) {
        const res = await fetch(`${this.opts.baseUrl}/v1/action/preview`, { method: 'POST', headers: await this.headers(true), credentials: 'include', body: JSON.stringify({ app: this.opts.app, actionId, token }) });
        const json = (await res.json().catch(() => ({})));
        if (!res.ok)
            throw new Error(json.message || json.error || `preview ${res.status}`);
        return { ...json, supported: Boolean(json.supported) };
    }
    /**
     * The requester inbox. A failure carries `retryAfterMs` when the server said when to come back (429).
     * Deliberately a SIMPLE cross-origin request: no content-type (there is no body) and no `cache` mode, so a
     * browser sends no CORS preflight before each poll. gyro-svc marks the answer `private, no-store` itself.
     */
    async listActions(options = {}) {
        const qs = new URLSearchParams({ app: this.opts.app });
        const res = await fetch(`${this.opts.baseUrl}/v1/actions?${qs}`, { headers: await this.headers(), credentials: 'include', signal: options.signal });
        if (!res.ok)
            throw await this.httpError('actions', res);
        const json = await res.json();
        return json.actions ?? [];
    }
    async claimActionResult(actionId, token) {
        const res = await fetch(`${this.opts.baseUrl}/v1/action/result`, {
            method: 'POST',
            headers: await this.headers(true),
            credentials: 'include',
            body: JSON.stringify({ app: this.opts.app, actionId, token }),
        });
        const json = (await res.json().catch(() => ({})));
        if (!res.ok && res.status !== 202)
            throw new Error(json.message || json.error || `action result ${res.status}`);
        return { ...json, status: json.status ?? 'pending' };
    }
    async undoAction(actionId) {
        const res = await fetch(`${this.opts.baseUrl}/v1/action/undo`, { method: 'POST', headers: await this.headers(true, false), credentials: 'include', body: JSON.stringify({ app: this.opts.app, actionId }) });
        return res.json();
    }
    /**
     * Forward a widget runtime error to gyro-svc so it reaches the central Sentry — without bundling a browser
     * SDK into the embedded widget (which could clash with the host app's own Sentry). Best-effort; never throws.
     */
    async reportError(info) {
        try {
            await fetch(`${this.opts.baseUrl}/v1/client-error`, {
                method: 'POST',
                headers: await this.headers(true),
                credentials: 'include',
                keepalive: true,
                body: JSON.stringify({
                    app: this.opts.app,
                    surface: 'widget',
                    message: String(info.message).slice(0, 1000),
                    stack: info.stack ? String(info.stack).slice(0, 6000) : undefined,
                    where: info.where,
                }),
            });
        }
        catch {
            /* error reporting must never throw */
        }
    }
    /** Quality feedback (thumbs) for a turn, joined to the trace by traceId. */
    async feedback(traceId, rating, reasonTags) {
        try {
            await fetch(`${this.opts.baseUrl}/v1/feedback`, { method: 'POST', headers: await this.headers(true), credentials: 'include', body: JSON.stringify({ traceId, rating, reasonTags }) });
        }
        catch {
            /* best-effort */
        }
    }
    /**
     * Pulse feedback (docs/CONTRACT-pulse.md section 6.1): the quick feedback card (`kind: 'card'`, with `rating`, `undo` and
     * the cause chips) or a thumb (`kind: 'thumb'`, with the thumb and an optional comment). Only the body's known fields
     * travel (the route refuses unknown ones); the comment is trimmed and clipped to 500 characters, the tags to 3. The
     * answer is never thrown: `{ ok: false, status, error }` carries the service's refusal for the card to show.
     */
    async pulseFeedback(body) {
        const comment = typeof body.comment === 'string' ? body.comment.trim().slice(0, PULSE_COMMENT_MAX_CHARS) : '';
        const tags = Array.isArray(body.tags) ? body.tags.filter((tag) => typeof tag === 'string' && tag.trim().length > 0).slice(0, PULSE_TAGS_MAX) : [];
        const payload = { askId: body.askId, kind: body.kind };
        if (body.actionId)
            payload.actionId = body.actionId;
        if (body.kind === 'card') {
            if (body.rating)
                payload.rating = body.rating;
            if (typeof body.undo === 'boolean')
                payload.undo = body.undo;
        }
        if (body.kind === 'thumb' && body.thumb)
            payload.thumb = body.thumb;
        if (tags.length)
            payload.tags = tags;
        if (comment)
            payload.comment = comment;
        try {
            const res = await fetch(`${this.opts.baseUrl}/v1/pulse/feedback`, { method: 'POST', headers: await this.headers(true), credentials: 'include', body: JSON.stringify(payload) });
            if (res.ok)
                return { ok: true };
            const failure = await this.httpError('pulse feedback', res);
            return { ok: false, status: res.status, error: failure.message };
        }
        catch (error) {
            return { ok: false, status: 0, error: error instanceof Error ? error.message : 'request failed' };
        }
    }
    pulseChipCache = new Map();
    /**
     * The three cause chips for a tool (contract section 10.4: `GET /v1/pulse/chips?tool=`), kept an hour per tool on this
     * client. The answer is `{ chips: string[] }` (a bare array is taken too); anything else, a refusal or a failed request
     * answers the tool family's defaults (pulseCards.ts `defaultPulseChips`) and is not cached, so a dark service costs one
     * request per card and the card still has its chips.
     */
    async pulseChips(tool, options = {}) {
        const now = options.now ?? Date.now;
        const key = tool.trim().slice(0, 120);
        const cached = this.pulseChipCache.get(key);
        if (cached && cached.until > now())
            return cached.chips;
        try {
            const res = await fetch(`${this.opts.baseUrl}/v1/pulse/chips?${new URLSearchParams({ app: this.opts.app, tool: key })}`, { headers: await this.headers(), credentials: 'include', signal: options.signal });
            if (!res.ok)
                return defaultPulseChips(key);
            const chips = parsePulseChips(await res.json());
            if (!chips)
                return defaultPulseChips(key);
            this.pulseChipCache.set(key, { chips, until: now() + PULSE_CHIPS_TTL_MS });
            return chips;
        }
        catch {
            return defaultPulseChips(key);
        }
    }
    // ---- Server-synced history ----
    /** Make a tool-free handoff; never mutates history or starts a new chat on the caller's behalf. */
    async compactConversation(request, options = {}) {
        const controller = new AbortController();
        const cancel = () => controller.abort(options.signal?.reason);
        options.signal?.addEventListener('abort', cancel, { once: true });
        const timeout = setTimeout(() => controller.abort(new CompactionError('summary_timeout', 504)), 100_000);
        try {
            if (options.signal?.aborted)
                cancel();
            controller.signal.throwIfAborted();
            const headers = await this.headers(true);
            controller.signal.throwIfAborted();
            const res = await fetch(`${this.opts.baseUrl}/v1/history/compact`, {
                method: 'POST', headers, credentials: 'include', signal: controller.signal,
                body: JSON.stringify({ app: this.opts.app, conversationId: request.conversationId,
                    turns: request.turns?.map(({ role, content }) => ({ role, content })), model: request.model }),
            });
            if (!res.ok) {
                const failure = await res.json().catch(() => null);
                const code = typeof failure?.error === 'string' ? failure.error : 'summary_unavailable';
                // Older services do not expose the compaction route. Distinguish that from
                // an existing route reporting a missing conversation; repeating cannot add it.
                // A usage-limit refusal keeps gyro-svc's sentence naming the limit (CompactionError shows only those).
                throw new CompactionError([404, 405, 501].includes(res.status) && code !== 'conversation_not_found' ? 'summary_not_supported' : code, res.status, failure?.message);
            }
            const result = await res.json().catch(() => null);
            if (!result || typeof result.summary !== 'string' || !result.summary.trim() || result.summary.length > 6000
                || typeof result.model !== 'string' || !result.model || typeof result.traceId !== 'string' || !result.traceId
                || !Number.isSafeInteger(result.sourceTurnCount) || Number(result.sourceTurnCount) < 1 || Number(result.sourceTurnCount) > 1000) {
                throw new CompactionError('summary_incomplete', 502);
            }
            return result;
        }
        catch (error) {
            if (controller.signal.aborted)
                throw controller.signal.reason;
            if (error instanceof CompactionError)
                throw error;
            throw new CompactionError('summary_unavailable', 0);
        }
        finally {
            clearTimeout(timeout);
            options.signal?.removeEventListener('abort', cancel);
        }
    }
    async listHistory(search) {
        try {
            const qs = new URLSearchParams({ app: this.opts.app });
            if (search)
                qs.set('search', search);
            const res = await fetch(`${this.opts.baseUrl}/v1/history?${qs}`, { headers: await this.headers(), credentials: 'include' });
            // An auth/HTTP failure here is NOT "no conversations" — a spent/expired credential would otherwise
            // render as a silently-empty list. Keep the []-return contract for callers, but forward the diagnostic
            // (HTTP status + server reason) to the central error sink so the failure is visible, not swallowed.
            if (!res.ok) {
                const err = await this.httpError('history', res);
                void this.reportError({ message: err.message, where: 'listHistory' });
                return [];
            }
            return (await res.json()).conversations ?? [];
        }
        catch {
            return [];
        }
    }
    async getHistory(id) {
        const load = await this.loadHistory(id);
        return load.status === 'loaded' ? load.conversation : null;
    }
    /** Fetch one conversation and say how it went: loaded, missing on the server, or failed. Never throws. */
    async loadHistory(id) {
        try {
            const res = await fetch(`${this.opts.baseUrl}/v1/history?app=${encodeURIComponent(this.opts.app)}&id=${encodeURIComponent(id)}`, { headers: await this.headers(), credentials: 'include' });
            // Forward the diagnostic so a spent credential that renders as an empty transcript is visible in the
            // central error sink.
            if (!res.ok) {
                const err = await this.httpError('history', res);
                void this.reportError({ message: err.message, where: 'getHistory' });
                return { status: 'failed' };
            }
            const conversation = (await res.json()).conversation;
            if (!conversation)
                return { status: 'missing' };
            // A body that is not a transcript is a failed load, not an empty conversation.
            if (!Array.isArray(conversation.turns))
                return { status: 'failed' };
            return { status: 'loaded', conversation };
        }
        catch {
            return { status: 'failed' };
        }
    }
    /**
     * Mirror a conversation to gyro-svc. True only for a 2xx: a refusal (a v61 custody 401/403, a 5xx) is not a
     * save, so the caller must not remember it as one. Its diagnostic (HTTP status + server reason) goes to the
     * central error sink rather than being swallowed. Never throws.
     */
    async saveHistory(conversation) {
        return (await this.saveHistoryOutcome(conversation)).ok;
    }
    /**
     * saveHistory with the reason: a 413 (conversation_too_large), 400 (invalid_conversation) or 507 (history storage
     * full) is a refusal of this transcript and is reported as not retryable, with the server's message and limit, so the
     * widget can show it rather than PUT the same bytes again on every change. Never throws.
     */
    async saveHistoryOutcome(conversation) {
        try {
            const res = await fetch(`${this.opts.baseUrl}/v1/history`, { method: 'PUT', headers: await this.headers(true), credentials: 'include', body: JSON.stringify({ app: this.opts.app, conversation }), signal: historyWriteSignal() });
            if (res.ok)
                return { ok: true, status: res.status, retryable: false };
            let body = {};
            try {
                body = JSON.parse(await res.text());
            }
            catch {
                /* a body that is not JSON still leaves the status */
            }
            const error = typeof body.error === 'string' ? body.error : undefined;
            const message = typeof body.message === 'string' ? body.message : undefined;
            const limit = typeof body.limit === 'number' ? body.limit : undefined;
            void this.reportError({ message: `history ${res.status}${error ? `: ${error}` : ''}${message ? ` (${message.slice(0, 200)})` : ''}`, where: 'saveHistory' });
            return { ok: false, status: res.status, error, message, limit, retryable: !HISTORY_REFUSAL_STATUSES.has(res.status) };
        }
        catch {
            return { ok: false, retryable: true };
        }
    }
    /**
     * Fork a saved chat into a new one: every turn, or the turns up to and including `upToTurn`. The source chat is not
     * changed. Throws a ForkError naming why when gyro-svc refused (missing, too large, storage full) or has no fork route.
     */
    async forkHistory(request) {
        let res;
        try {
            res = await fetch(`${this.opts.baseUrl}/v1/history/fork`, {
                method: 'POST', headers: await this.headers(true), credentials: 'include', signal: historyWriteSignal(),
                body: JSON.stringify({ app: this.opts.app, id: request.id, ...(request.upToTurn !== undefined ? { upToTurn: request.upToTurn } : {}), ...(request.title !== undefined ? { title: request.title } : {}) }),
            });
        }
        catch {
            throw new ForkError('fork_unavailable', 0);
        }
        if (!res.ok) {
            const failure = await res.json().catch(() => null);
            const code = typeof failure?.error === 'string' ? failure.error : 'fork_unavailable';
            void this.reportError({ message: `history fork ${res.status}: ${code}`, where: 'forkHistory' });
            throw new ForkError([404, 405, 501].includes(res.status) && code !== 'conversation_not_found' ? 'fork_not_supported' : code, res.status);
        }
        const result = await res.json().catch(() => null);
        if (!result || typeof result.id !== 'string' || !result.id || typeof result.title !== 'string' || !Number.isSafeInteger(result.turnCount)) {
            throw new ForkError('fork_incomplete', 502);
        }
        return result;
    }
    /** Confirm the server accepted deletion; callers must retain the chat when this returns false. */
    async deleteHistory(id) {
        try {
            const res = await fetch(`${this.opts.baseUrl}/v1/history?app=${encodeURIComponent(this.opts.app)}&id=${encodeURIComponent(id)}`, { method: 'DELETE', headers: await this.headers(false, true), credentials: 'include', signal: historyWriteSignal() });
            return res.ok;
        }
        catch {
            return false;
        }
    }
    /**
     * Create a short-lived shareable snapshot of an answer; returns the link to hand out, or `null` on
     * failure. The service answers `{ id, url: '/share/<id>' }`, but gyro-svc has NO `/share/:id` route:
     * behind the admin SPA that path falls through to index.html (the Entra sign-in page) and without
     * it to a JSON 404 — so a recipient could never open the old link (L-26). The link returned here is
     * the script-free print view `/v1/share/<id>/print`, the one unauthenticated page that renders the
     * share (also the documented PDF path). `url` is kept as a fallback for a future server that serves
     * a viewer at that path.
     */
    async share(title, body) {
        const outcome = await this.shareAnswer(title, body);
        return outcome && 'url' in outcome ? outcome.url : null;
    }
    /**
     * share() with the chat the answer belongs to (`conversationId`, so the service can refuse a link to a chat that holds pay
     * data) and the service's refusal kept: `{ refused }` carries the line the service answered a 403 `pay_data_not_shareable`
     * with, to show as it is. `null` on any other failure, as share() answers.
     */
    async shareAnswer(title, body, conversationId) {
        try {
            const res = await fetch(`${this.opts.baseUrl}/v1/share`, { method: 'POST', headers: await this.headers(true), credentials: 'include', body: JSON.stringify({ app: this.opts.app, title, body, ...(conversationId ? { conversationId } : {}) }) });
            if (res.status === 403) {
                const refusal = (await res.json().catch(() => ({})));
                return refusal.error === 'pay_data_not_shareable' ? { refused: typeof refusal.message === 'string' && refusal.message.trim() ? refusal.message.slice(0, 300) : PAY_DATA_NOT_SHAREABLE_TEXT } : null;
            }
            if (!res.ok)
                return null;
            const j = (await res.json());
            if (typeof j.id === 'string' && /^[a-f0-9]{16}$/i.test(j.id))
                return { url: `${this.opts.baseUrl}/v1/share/${j.id}/print` };
            return j.url ? { url: `${this.opts.baseUrl}${j.url}` } : null;
        }
        catch {
            return null;
        }
    }
    // ---- Per-user memory (the assistant's adaptive note about you; viewable + editable) ----
    /**
     * Fetch the user's memory. Returns `null` on failure (auth/network) — distinct from an EMPTY memory —
     * so the editor can show an error instead of a blank textarea that would overwrite the real note on Save.
     */
    async getMemory() {
        try {
            const res = await fetch(`${this.opts.baseUrl}/v1/memory?app=${encodeURIComponent(this.opts.app)}`, { headers: await this.headers(), credentials: 'include' });
            // null already blocks Save so a blank editor can't overwrite the real note (see the editor). Also
            // forward WHY the load failed (auth vs network) to the central sink instead of dropping it silently.
            if (!res.ok) {
                const err = await this.httpError('memory', res);
                void this.reportError({ message: err.message, where: 'getMemory' });
                return null;
            }
            return (await res.json());
        }
        catch {
            return null;
        }
    }
    /**
     * "Connect Asana from Gyro" (docs/DESIGN-asana-connect-from-gyro.md section 3), the desktop app only: start (or
     * resume) a vendor connection. On the desktop the overlay serves this natively: it pins and opens the authorize URL
     * itself and answers the page `{ status, family, expiresAt, pollAfterMs }` without it. Never throws on an HTTP status.
     */
    async connectStart(family, capability) {
        const res = await fetch(`${this.opts.baseUrl}/v1/person/connect-start`, {
            method: 'POST', headers: await this.headers(true), credentials: 'include',
            body: JSON.stringify({ app: this.opts.app, family, ...(capability ? { capability } : {}) }),
        });
        const body = await res.json().catch(() => ({}));
        return { status: res.status, body };
    }
    /** The person's vendor connections through the Bridge (GET /v1/person/connections): the Connectors page and the post-Connect poll. */
    async personConnections(options = {}) {
        const qs = new URLSearchParams({ app: this.opts.app });
        const res = await fetch(`${this.opts.baseUrl}/v1/person/connections?${qs}`, { headers: await this.headers(), credentials: 'include', signal: options.signal });
        const body = await res.json().catch(() => ({}));
        return { status: res.status, body };
    }
    /** Stop Gyro using a vendor (`revoke`) or remove the connection (`disconnect`); the desktop confirms natively before it is sent. */
    async changePersonConnection(family, action) {
        const res = await fetch(`${this.opts.baseUrl}/v1/person/connections/${family}/${action}`, {
            method: 'POST', headers: await this.headers(true), credentials: 'include', body: JSON.stringify({ app: this.opts.app }),
        });
        const body = await res.json().catch(() => ({}));
        return { status: res.status, body };
    }
    async listConnectors() {
        const res = await fetch(`${this.opts.baseUrl}/v1/connectors?app=${encodeURIComponent(this.opts.app)}`, {
            headers: await this.headers(), credentials: 'include',
        });
        if (!res.ok)
            throw await this.httpError('connectors', res);
        const result = await res.json();
        return Array.isArray(result.connectors) ? result.connectors : [];
    }
    /** Replace the user's memory. Returns the canonical saved text (server-capped), or `null` on failure. */
    async saveMemory(text) {
        try {
            const res = await fetch(`${this.opts.baseUrl}/v1/memory`, { method: 'PUT', headers: await this.headers(true), credentials: 'include', body: JSON.stringify({ app: this.opts.app, text }) });
            if (!res.ok)
                return null;
            const j = (await res.json());
            return typeof j.text === 'string' ? j.text : String(text);
        }
        catch {
            return null;
        }
    }
    /**
     * The person's goals, kept as `Goal:` lines of the same memory note (the composer's /goal slash command; owner,
     * 2026-10-31): list them, add one (`text`) or remove the n-th (`n`, 1-based). POST /v1/memory/goals, the same identity
     * as the memory edit. Never throws on an HTTP status: the caller reads `status` and the body's `error` code
     * (goal_empty, goal_too_long, goal_limit, goal_duplicate, goal_not_found, memory_too_large, memory_unavailable).
     */
    async memoryGoals(action, input = {}) {
        const res = await fetch(`${this.opts.baseUrl}/v1/memory/goals`, {
            method: 'POST', headers: await this.headers(true), credentials: 'include',
            body: JSON.stringify({ app: this.opts.app, action, ...(input.text !== undefined ? { text: input.text } : {}), ...(input.n !== undefined ? { n: input.n } : {}) }),
        });
        const body = await res.json().catch(() => ({}));
        return { status: res.status, body };
    }
    // ---- The desktop mascot gallery (the desktop app only; the agent forwards these routes under device custody) ----
    /**
     * The person's gallery and pick, asked with `thumbnails=url` (the 2,000 gallery): a gyro-svc that knows the query
     * answers each outfit's thumbnail as a URL (getMascotThumbnail reads it when the tile is shown), an older one inline as
     * before; the view carries either. Throws a MascotLookError.
     */
    async getMascotLooks() {
        return this.mascotLooksRequest('GET', withZone('/v1/mascot/looks', new URLSearchParams({ thumbnails: 'url' })));
    }
    /**
     * Store the person's look: `'default'`, an outfit id in the gallery now, or `null` (automatic). Answers the new view
     * (thumbnails by URL, as getMascotLooks). Throws a MascotLookError; `unavailable` when the outfit has left the gallery
     * (409 `mascot_look_unavailable`).
     */
    async setMascotLook(look) {
        return this.mascotLooksRequest('PUT', withZone('/v1/mascot/look', new URLSearchParams({ thumbnails: 'url' })), { look });
    }
    /**
     * Start (or edit) the person's outfit loop (PUT /v1/mascot/rotation; outfit rotation, owner 2026-10-19): the outfits
     * in picked order, every so many hours or days (a days loop at `changeTime` on this PC's clock: the zone goes with
     * the request), in picked or random order. Answers the new view, its `rotation` the loop. Throws a MascotLookError;
     * `unavailable` when an outfit has left the person's gallery (409 `mascot_look_unavailable`).
     */
    async setMascotRotation(definition) {
        return this.mascotLooksRequest('PUT', withZone('/v1/mascot/rotation', new URLSearchParams({ thumbnails: 'url' })), definition);
    }
    /** Pause the loop (the saved pick shows) or resume it (by the clock). Answers the new view. Throws a MascotLookError. */
    async setMascotRotationPaused(paused) {
        return this.mascotLooksRequest('PUT', withZone('/v1/mascot/rotation', new URLSearchParams({ thumbnails: 'url' })), { paused });
    }
    /** Stop the loop: the outfit it is at becomes the saved pick, so nothing changes on screen. Answers the new view. Throws a MascotLookError. */
    async stopMascotRotation() {
        return this.mascotLooksRequest('DELETE', withZone('/v1/mascot/rotation', new URLSearchParams({ thumbnails: 'url' })));
    }
    /**
     * One outfit's gallery thumbnail (GET /v1/mascot/outfits/:id/thumbnail), base64 PNG bytes, for a tile whose view
     * carried `thumbnailUrl`. The agent forwards it under device custody like the looks; the browser caches it by the
     * image's hash for a day. Throws a MascotLookError.
     */
    async getMascotThumbnail(outfitId) {
        if (!isMascotOutfitId(outfitId))
            throw new MascotLookError('invalid_request', 0);
        let res;
        try {
            res = await fetch(`${this.opts.baseUrl}${withZone(`/v1/mascot/outfits/${outfitId}/thumbnail`)}`, { headers: await this.headers(), credentials: 'include' });
        }
        catch {
            throw new MascotLookError('network', 0);
        }
        if (!res.ok) {
            const answer = (await res.json().catch(() => null));
            throw new MascotLookError(typeof answer?.error === 'string' && /^[a-z0-9_]{1,64}$/.test(answer.error) ? answer.error : '', res.status);
        }
        const body = (await res.json().catch(() => null));
        const thumbnail = parseMascotThumbnail(body, outfitId);
        if (!thumbnail)
            throw new MascotLookError('invalid_response', res.status);
        return thumbnail;
    }
    /**
     * Several outfits' gallery thumbnails in one request (GET /v1/mascot/thumbnails?ids=…, widget 0.16.8), up to
     * MASCOT_THUMBNAILS_BATCH_MAX ids, each once: one request per tile tripped gyro-svc's per-device request floor on a
     * gallery of 600 (2026-10-11), and a batch is one request. Answers what was read, which ids are not in the gallery and
     * which could not be read now. Throws a MascotLookError: a 404 or 400 from a gyro-svc (or a desktop app) that does not
     * know the route, which the caller then answers with single reads; a 429 carries how long to wait.
     */
    async getMascotThumbnails(outfitIds) {
        if (outfitIds.length === 0 || outfitIds.length > MASCOT_THUMBNAILS_BATCH_MAX || outfitIds.some((id) => !isMascotOutfitId(id)) || new Set(outfitIds).size !== outfitIds.length) {
            throw new MascotLookError('invalid_request', 0);
        }
        const res = await this.mascotRead(withZone('/v1/mascot/thumbnails', this.mascotReadParams({ ids: outfitIds.join(',') })));
        const batch = parseMascotThumbnailBatch(await res.json().catch(() => null), outfitIds);
        if (!batch)
            throw new MascotLookError('invalid_response', res.status);
        return batch;
    }
    /**
     * One outfit's full PNG (GET /v1/mascot/outfits/:id/image, widget 0.16.8), base64 with its pixel size, for the gallery's
     * hover preview; the browser caches it by the image's hash for a day. Throws a MascotLookError.
     */
    async getMascotImage(outfitId) {
        if (!isMascotOutfitId(outfitId))
            throw new MascotLookError('invalid_request', 0);
        const res = await this.mascotRead(withZone(`/v1/mascot/outfits/${outfitId}/image`, this.mascotReadParams()));
        const image = parseMascotImage(await res.json().catch(() => null), outfitId);
        if (!image)
            throw new MascotLookError('invalid_response', res.status);
        return image;
    }
    /**
     * The animation preview of an outfit that has animations (GET /v1/mascot/outfits/:id/actions-preview, widget 0.17.3): a
     * sprite sheet of its primary clip, base64, with its frame count, fps, frame size and columns, for the gallery's
     * enlargement; the browser caches it by the pack for a day. Throws a MascotLookError (a 404 is an outfit without one).
     */
    async getMascotActionsPreview(outfitId) {
        if (!isMascotOutfitId(outfitId))
            throw new MascotLookError('invalid_request', 0);
        const res = await this.mascotRead(withZone(`/v1/mascot/outfits/${outfitId}/actions-preview`, this.mascotReadParams()));
        const preview = parseMascotActionsPreview(await res.json().catch(() => null), outfitId);
        if (!preview)
            throw new MascotLookError('invalid_response', res.status);
        return preview;
    }
    /** The query a mascot read sends besides the zone: the Office pane names itself (gyro-svc checks its own proof then); every other app is the desktop's. */
    mascotReadParams(params = {}) {
        return new URLSearchParams(this.opts.app === 'office' ? { ...params, app: 'office' } : params);
    }
    /** A mascot GET that answered 2xx. Throws a MascotLookError otherwise (`network` with status 0 when no answer came; a 429 carries its wait). */
    async mascotRead(path) {
        let res;
        try {
            res = await fetch(`${this.opts.baseUrl}${path}`, { headers: await this.headers(), credentials: 'include' });
        }
        catch {
            throw new MascotLookError('network', 0);
        }
        if (!res.ok) {
            const answer = (await res.json().catch(() => null));
            const code = typeof answer?.error === 'string' && /^[a-z0-9_]{1,64}$/.test(answer.error) ? answer.error : '';
            throw new MascotLookError(code, res.status, res.status === 429 ? parseRetryAfterMs(res.headers.get('retry-after'), answer ?? undefined) : undefined);
        }
        return res;
    }
    /** `path`: the full path with its query (withZone). */
    async mascotLooksRequest(method, path, body) {
        let res;
        try {
            res = await fetch(`${this.opts.baseUrl}${path}`, {
                method, headers: await this.headers(body !== undefined, method !== 'GET'), credentials: 'include',
                ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
            });
        }
        catch {
            throw new MascotLookError('network', 0);
        }
        if (!res.ok) {
            const answer = (await res.json().catch(() => null));
            const code = typeof answer?.error === 'string' && /^[a-z0-9_]{1,64}$/.test(answer.error) ? answer.error : '';
            const error = new MascotLookError(code, res.status);
            // As with memory: say why the gallery could not load (auth, custody, an older service) instead of dropping it.
            if (method === 'GET')
                void this.reportError({ message: error.message, where: 'getMascotLooks' });
            throw error;
        }
        const view = parseMascotLooksView(await res.json().catch(() => null));
        if (!view)
            throw new MascotLookError('invalid_response', res.status);
        return view;
    }
    /**
     * Give an outfit in the person's gallery 1 to 5 stars, or change them (PUT /v1/mascot/outfits/:id/rating), or take them
     * back with `null` (DELETE). gyro-svc takes ratings only from the desktop app (its overlay forwards both under full device
     * custody) and the Office pane, so the call names this client's app, and the person's zone as `tz` like every mascot
     * call (a day outfit can get a first rating on its day in their zone). Answers the person's stars and, when gyro-svc
     * could read them, the outfit's new average and count. Throws a MascotRatingError; a 429's carries how long to wait.
     * Reports nothing anywhere: whose stars an outfit got stays out of every log, error report and console.
     */
    async rateMascotOutfit(outfitId, stars) {
        if (!isMascotOutfitId(outfitId) || (stars !== null && !isMascotStars(stars)))
            throw new MascotRatingError('invalid_request', 0);
        const path = withZone(`/v1/mascot/outfits/${outfitId}/rating`, new URLSearchParams({ app: this.opts.app }));
        let res;
        try {
            res = await fetch(`${this.opts.baseUrl}${path}`, stars === null
                ? { method: 'DELETE', headers: await this.headers(false, true), credentials: 'include' }
                : { method: 'PUT', headers: await this.headers(true), credentials: 'include', body: JSON.stringify({ stars }) });
        }
        catch {
            throw new MascotRatingError('network', 0);
        }
        const body = (await res.json().catch(() => null));
        if (!res.ok) {
            const code = typeof body?.error === 'string' && /^[a-z0-9_]{1,64}$/.test(body.error) ? body.error : '';
            // The desktop overlay relays only the body, so a 429's wait is read from its `retryAfter` as well as the header.
            throw new MascotRatingError(code, res.status, res.status === 429 ? parseRetryAfterMs(res.headers.get('retry-after'), body ?? undefined) : undefined);
        }
        const answer = parseMascotRatingAnswer(body, outfitId);
        if (!answer)
            throw new MascotRatingError('invalid_response', res.status);
        return answer;
    }
    /**
     * GET /v1/skills: the person's saved skills and the company's, with whether this identity may save here (`verified`).
     * Read with the host's auth headers and cookies like the memory and PPP reads (on the desktop the agent forwards it
     * natively). A service without the feature answers 404, which throws like any other refusal.
     */
    async listSkills(options = {}) {
        const res = await fetch(`${this.opts.baseUrl}${withZone('/v1/skills', new URLSearchParams({ app: this.opts.app }))}`, { headers: await this.headers(), credentials: 'include', signal: options.signal });
        if (!res.ok)
            throw await skillRefusal(res);
        const body = (await res.json().catch(() => null));
        const skills = Array.isArray(body?.skills) ? body.skills.map(skillItem).filter((item) => item !== null) : [];
        return {
            skills,
            limits: { skills: typeof body?.limits?.skills === 'number' ? body.limits.skills : 20, body: typeof body?.limits?.body === 'number' ? body.limits.body : 8000 },
            verified: body?.verified === true,
        };
    }
    /**
     * GET /v1/skills/:id: one of the person's own skills with its steps (a rename reads it, then saves it back with the new
     * name). gyro-svc answers it only to an identity it verified (403 skills_custody_required otherwise) and only for the
     * person's own skill (404 otherwise); a refusal throws a SkillRequestError.
     */
    async getSkill(id) {
        if (!isSkillId(id) || !id.startsWith('u-'))
            throw new SkillRequestError('skill_not_found', 404);
        const res = await fetch(`${this.opts.baseUrl}${withZone(`/v1/skills/${id}`, new URLSearchParams({ app: this.opts.app }))}`, { headers: await this.headers(), credentials: 'include' });
        if (!res.ok)
            throw await skillRefusal(res);
        return savedSkill(await res.json().catch(() => null));
    }
    /**
     * PUT /v1/skills/:id: save one of the person's skills (`'new'` creates one; an existing id replaces that skill, so a
     * rename sends the steps back with the new name). Answers the saved skill; a refusal throws a SkillRequestError (a
     * 413 names the limit, a 403 `skills_custody_required` means this host cannot save, a 404 an id that is not theirs).
     */
    async saveSkill(id, input) {
        if (id !== 'new' && !isSkillId(id))
            throw new SkillRequestError('skill_not_found', 404);
        const res = await fetch(`${this.opts.baseUrl}${withZone(`/v1/skills/${id}`, new URLSearchParams({ app: this.opts.app }))}`, {
            method: 'PUT', headers: await this.headers(true), credentials: 'include',
            body: JSON.stringify({ name: input.name, description: input.description ?? '', aliases: input.aliases ?? [], body: input.body }),
        });
        if (!res.ok)
            throw await skillRefusal(res);
        return savedSkill(await res.json().catch(() => null));
    }
    /** DELETE /v1/skills/:id: remove one of the person's skills. True when it is gone (204), false when it was not theirs (404). */
    async deleteSkill(id) {
        if (!isSkillId(id))
            return false;
        const res = await fetch(`${this.opts.baseUrl}${withZone(`/v1/skills/${id}`, new URLSearchParams({ app: this.opts.app }))}`, { method: 'DELETE', headers: await this.headers(false, true), credentials: 'include' });
        if (res.status === 404)
            return false;
        if (!res.ok)
            throw await skillRefusal(res);
        return true;
    }
    /**
     * POST /v1/skills/from-package: save an attached .skill package (SKILL_PACKAGE_MEDIA_TYPE, base64) as a new skill, on
     * the person's explicit ask only; what they typed for the name, description or aliases wins over the package's own.
     */
    async saveSkillFromPackage(attachment, overrides = {}) {
        if (attachment.mediaType !== SKILL_PACKAGE_MEDIA_TYPE)
            throw new SkillRequestError('unsupported_media_type', 400, `That file is not a .skill package (${SKILL_PACKAGE_MEDIA_TYPE}).`);
        const res = await fetch(`${this.opts.baseUrl}${withZone('/v1/skills/from-package', new URLSearchParams({ app: this.opts.app }))}`, {
            method: 'POST', headers: await this.headers(true), credentials: 'include',
            body: JSON.stringify({ attachment: { mediaType: attachment.mediaType, data: attachment.data }, ...overrides }),
        });
        if (!res.ok)
            throw await skillRefusal(res);
        return savedSkill(await res.json().catch(() => null));
    }
    /**
     * The desktop's look check (#291): the gallery view, or `{ unchanged }` while `since` is still its validator (gyro-svc
     * sends one, `etag`, only with the look on). Asked with `thumbnails=url` like getMascotLooks (a 2,000-outfit gallery
     * inline would be megabytes per check); the caller reads the one thumbnail it shows (gyroLook.ts desktopLookCheck),
     * and an older gyro-svc still answers inline. Unlike getMascotLooks it reports nothing when it fails: the check
     * retries on its own backoff, and a report per retry would flood the error sink (review L7). Throws a MascotLookError.
     */
    async checkMascotLooks(since) {
        let res;
        try {
            res = await fetch(`${this.opts.baseUrl}${withZone('/v1/mascot/looks', new URLSearchParams({ ...(since ? { since } : {}), thumbnails: 'url' }))}`, { headers: await this.headers(), credentials: 'include' });
        }
        catch {
            throw new MascotLookError('network', 0);
        }
        if (!res.ok) {
            const answer = (await res.json().catch(() => null));
            throw new MascotLookError(typeof answer?.error === 'string' && /^[a-z0-9_]{1,64}$/.test(answer.error) ? answer.error : '', res.status);
        }
        const body = (await res.json().catch(() => null));
        const unchanged = body?.unchanged === true ? parseGyroLookAnswer(body) : null;
        if (unchanged && 'unchanged' in unchanged)
            return unchanged;
        const view = parseMascotLooksView(body);
        if (!view)
            throw new MascotLookError('invalid_response', res.status);
        const etag = looksViewEtag({ ...view, etag: body?.etag });
        return etag ? { ...view, etag } : view;
    }
    // ---- Gyro's look everywhere (#291): the person's look in any app, through this client's own auth ----
    /**
     * The person's current look on this app (GET /v1/mascot/current): the built-in Gyro, or an outfit with its two
     * renditions as data: URIs; `since` (the last ETag) answers `{ unchanged: true }` when nothing changed. `xl`: the
     * answer carries the 300 px rendition as well (the Office pane's welcome picture). Throws a MascotLookError,
     * `invalid_response` for anything that is not a well-formed look.
     */
    async getLook(since, opts = {}) {
        const path = withZone('/v1/mascot/current', new URLSearchParams({ app: this.opts.app, ...(since ? { since } : {}), ...(opts.xl ? { xl: '1' } : {}) }));
        let res;
        try {
            res = await fetch(`${this.opts.baseUrl}${path}`, { headers: await this.headers(), credentials: 'include' });
        }
        catch {
            throw new MascotLookError('network', 0);
        }
        if (!res.ok) {
            const answer = (await res.json().catch(() => null));
            throw new MascotLookError(typeof answer?.error === 'string' && /^[a-z0-9_]{1,64}$/.test(answer.error) ? answer.error : '', res.status);
        }
        const look = parseGyroLookAnswer(await res.json().catch(() => null));
        if (!look)
            throw new MascotLookError('invalid_response', res.status);
        return look;
    }
    // ---- The Office pane's client-run tools and actions (contract §5) ----
    /**
     * Post a host-run tool's answer to a `pane_tool_call` with this client's auth. Never throws on an HTTP status: 404 and
     * 409 mean gyro-svc already closed the call, 403 that the call is not this caller's. Only gyro-svc `/v1/...` paths
     * are accepted.
     */
    async postClientToolResult(path, body, options = {}) {
        return this.postToService(path, body, options.signal);
    }
    /** Acknowledge a confirmed action the host executed itself: `{actionId, token, ok, result?, error?}` to its ack path. */
    async ackClientAction(path, body, options = {}) {
        return this.postToService(path, body, options.signal);
    }
    async postToService(path, body, signal) {
        if (!isServiceApiPath(path))
            throw new Error('not a gyro-svc /v1 path');
        const res = await fetch(`${this.opts.baseUrl}${path}`, { method: 'POST', headers: await this.headers(true), credentials: 'include', body: JSON.stringify(body), signal });
        const answer = await res.json().catch(() => ({}));
        return { status: res.status, body: answer };
    }
}
/**
 * How long the server asked us to wait, in ms: the larger of the Retry-After header (seconds or an HTTP date)
 * and the JSON body's `retryAfterMs` / `retryAfter` (seconds, as gyro-svc sends it). The body matters: a
 * cross-origin page cannot read an unexposed Retry-After header, and the desktop overlay relays only the body.
 */
export function parseRetryAfterMs(header, body, now = Date.now()) {
    const candidates = [];
    const text = header?.trim();
    if (text) {
        if (/^\d+(\.\d+)?$/.test(text))
            candidates.push(Number(text) * 1000);
        else {
            const at = Date.parse(text);
            if (Number.isFinite(at))
                candidates.push(at - now);
        }
    }
    if (typeof body?.retryAfterMs === 'number')
        candidates.push(body.retryAfterMs);
    if (typeof body?.retryAfter === 'number')
        candidates.push(body.retryAfter * 1000);
    const valid = candidates.filter((value) => Number.isFinite(value) && value > 0);
    return valid.length ? Math.max(...valid) : undefined;
}
//# sourceMappingURL=client.js.map